import { createAsyncThunk } from '@reduxjs/toolkit';

// Services
import { CompanyService } from '../../services/company.service';

// Thunks
import { openNotification } from '../app-notifications/app-notifications.thunks';
import { fetchCompanyFee, fetchSpecialities } from '../core/core.thunks';
import { fetchCurrentCurrency } from '../currency/currency.thunks';

// Slices
import { CoreSliceActions } from '../core/core.slice';
import { CompanySliceActions } from './company.slice';
import { UserSliceActions } from '../user/user.slice';

// Models
import {
  ICreateSpecialtySpecialisations,
  IUpdateCompanyBusinessAddress,
  IUpdateCompanyContactDetails, IUpdateSpecialtySpecialisations,
} from '../../interfaces/company.interfaces';

export enum ECompanyProfileThunks {
  FetchCompanyProfile = 'COMPANY/fetchCompanyProfile',
  UpdateCompanyName = 'COMPANY/updateCompanyName',
  UpdateCompanyWebUrl = 'COMPANY/updateCompanyWebUrl',
  UpdateCompanyAbout = 'COMPANY/updateCompanyAbout',
  FetchStipeAccountLink = 'COMPANY/fetchStipeAccountLink',
  ConnectStripeToCompany = 'COMPANY/connectStripeToCompany',
  UpdateCompanyContactDetails = 'COMPANY/updateCompanyContactDetails',
  UpdateCompanyBusinessAddress = 'COMPANY/updateCompanyBusinessAddress',
  UpdateCompanyCountries = 'COMPANY/updateCompanyCountries',
  UpdateCompanyLanguages = 'COMPANY/updateCompanyLanguages',
  UpdateCompanyCounsellorManagement = 'COMPANY/updateCompanyCounsellorManagement',
  UpdateCompanyCounsellorAttributes = 'COMPANY/updateCompanyCounsellorAttributes',
  CreateCompanySpecialtySpecialisations = 'COMPANY/createCompanySpecialtySpecialisations',
  UpdateCompanySpecialtySpecialisations = 'COMPANY/updateCompanySpecialtySpecialisations',
  DeleteCompanySpecialtySpecialisations = 'COMPANY/deleteCompanySpecialtySpecialisations',
  FetchCompanySpecialtySpecialisations = 'COMPANY/fetchCompanySpecialtySpecialisations',
  FetchCompanyAllAvailableCountries = 'COMPANY/fetchCompanyAllAvailableCountries',
  FetchCompanyAllAvailableLanguages = 'COMPANY/fetchCompanyAllAvailableLanguages',
  UpdateCompanyBilling = 'COMPANY/updateCompanyBilling',
}

export const fetchCompanyProfile = createAsyncThunk(
  ECompanyProfileThunks.FetchCompanyProfile,
  async (_, { dispatch }) => {
    dispatch(CompanySliceActions.companyRequest());
    const { company_profile, error } = await CompanyService.fetchCompanyProfile();
    if (!company_profile) {
      dispatch(CompanySliceActions.companyRequestFailure(error ?? 'Error loading company profile'));
      return null;
    }
    dispatch(CompanySliceActions.companyRequestSuccess(company_profile));
    return company_profile;
  }
);

export const updateCompanyName = createAsyncThunk(
  ECompanyProfileThunks.UpdateCompanyName,
  async (company_name: string, { dispatch }) => {
    dispatch(CompanySliceActions.updateCompanyRequest());
    const { company_profile, error } = await CompanyService.updateCompanyName(company_name);
    if (!company_profile) {
      dispatch(CompanySliceActions.updateCompanyRequestFailure(error ?? 'Couldn\'t update info, try again'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update info, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));

    dispatch(CompanySliceActions.updateCompanyRequestSuccess(company_profile));
    return company_profile;
  }
);

export const updateCompanyWebUrl = createAsyncThunk(
  ECompanyProfileThunks.UpdateCompanyWebUrl,
  async (url_code: string, { dispatch }) => {
    dispatch(CompanySliceActions.updateCompanyRequest());
    const { company_profile, error } = await CompanyService.updateCompanyWebUrl(url_code);
    if (!company_profile) {
      dispatch(CompanySliceActions.updateCompanyRequestFailure(error ?? 'Couldn\'t update info, try again'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update info, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));

    dispatch(CompanySliceActions.updateCompanyRequestSuccess(company_profile));
    return company_profile;
  }
);

export const updateCompanyAbout = createAsyncThunk(
  ECompanyProfileThunks.UpdateCompanyAbout,
  async (about: string, { dispatch }) => {
    dispatch(CompanySliceActions.updateCompanyRequest());
    const { company_profile, error } = await CompanyService.updateCompanyAbout(about);
    if (!company_profile) {
      dispatch(CompanySliceActions.updateCompanyRequestFailure(error ?? 'Couldn\'t update info, try again'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update info, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));

    dispatch(CompanySliceActions.updateCompanyRequestSuccess(company_profile));
    return company_profile;
  }
);

export const updateCompanyContactDetails = createAsyncThunk(
  ECompanyProfileThunks.UpdateCompanyContactDetails,
  async (data: IUpdateCompanyContactDetails, { dispatch }) => {
    dispatch(CompanySliceActions.updateCompanyRequest());
    const { company_profile, error } = await CompanyService.updateCompanyContactDetails(data);
    if (!company_profile) {
      dispatch(CompanySliceActions.updateCompanyRequestFailure(error ?? 'Couldn\'t update info, try again'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update info, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));

    dispatch(CompanySliceActions.updateCompanyRequestSuccess(company_profile));
    return company_profile;
  }
);

export const updateCompanyBusinessAddress = createAsyncThunk(
  ECompanyProfileThunks.UpdateCompanyBusinessAddress,
  async (data: IUpdateCompanyBusinessAddress, { dispatch }) => {
    dispatch(CompanySliceActions.updateCompanyRequest());
    const { company_profile, error } = await CompanyService.updateCompanyBusinessAddress(data);
    if (!company_profile) {
      dispatch(CompanySliceActions.updateCompanyRequestFailure(error ?? 'Couldn\'t update info, try again'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update info, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));

    dispatch(CompanySliceActions.updateCompanyRequestSuccess(company_profile));
    return company_profile;
  }
);

export const fetchStipeAccountLink = createAsyncThunk(
  ECompanyProfileThunks.FetchStipeAccountLink,
  async (data: { return_url: string, refresh_url: string }, { dispatch }) => {
    const { account_url, error } = await CompanyService.fetchStipeAccountLink(data);
    if (error) {
      dispatch(openNotification({
        description: error ?? 'Couldn\'t generate account link, try again',
        type: 'error'
      }));
      return null;
    }
    if (!account_url) {
      dispatch(openNotification({
        description: error ?? 'Change currently is not available',
        type: 'error'
      }));
    }
    return account_url;
  }
);

export const connectStripeToCompany = createAsyncThunk(
  ECompanyProfileThunks.ConnectStripeToCompany,
  async (account_id: string, { dispatch }) => {
    const { is_stripe_connected, error } = await CompanyService.connectStripeToCompany(account_id);
    if (!is_stripe_connected) {
      dispatch(openNotification({
        description: error ?? 'Stripe Account connecting is failed, please try again!',
        type: 'error'
      }));
      return false;
    }
    dispatch(openNotification({
      description: 'Stripe Account successfully connected!',
      type: 'success'
    }));

    dispatch(UserSliceActions.changeStripeConnectedStatus(is_stripe_connected));
    dispatch(CompanySliceActions.changeStripeConnectedStatus(is_stripe_connected));
    dispatch(fetchCompanyFee());
    dispatch(fetchCurrentCurrency());
    return is_stripe_connected;
  }
);

export const fetchCompanySpecialtySpecialisations = createAsyncThunk(
  ECompanyProfileThunks.FetchCompanySpecialtySpecialisations,
  async (_, { dispatch }) => {
    dispatch(CompanySliceActions.companyRequest());
    const { company_specializations, error } = await CompanyService.fetchCompanySpecialtySpecialisations();
    if (!company_specializations) {
      return null;
    }

    dispatch(CompanySliceActions.saveSpecialtySpecialisations(company_specializations));
    return company_specializations;
  }
);

export const fetchCompanyAllAvailableCountries = createAsyncThunk(
  ECompanyProfileThunks.FetchCompanyAllAvailableCountries,
  async (_, { dispatch }) => {
    const { countries, error } = await CompanyService.fetchCompanyAllAvailableCountries();
    if (!countries) {
      return null;
    }
    dispatch(CompanySliceActions.saveAllCountries(countries));
    return countries;
  }
);

export const fetchCompanyAllAvailableLanguages = createAsyncThunk(
  ECompanyProfileThunks.FetchCompanyAllAvailableLanguages,
  async (_, { dispatch }) => {
    const { languages, error } = await CompanyService.fetchCompanyAllAvailableLanguages();
    if (!languages) {
      return null;
    }
    dispatch(CompanySliceActions.saveAllLanguages(languages));
    return languages;
  }
);

export const updateCompanyCountries = createAsyncThunk(
  ECompanyProfileThunks.UpdateCompanyCountries,
  async (countryIds: string[], { dispatch }) => {
    const { countries, error } = await CompanyService.updateCompanyCountries(countryIds);
    if (!countries) {
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update info, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));

    dispatch(CoreSliceActions.loadCountriesSuccess(countries));
    return countries;
  }
);

export const updateCompanyLanguages = createAsyncThunk(
  ECompanyProfileThunks.UpdateCompanyLanguages,
  async (languageNames: string[], { dispatch }) => {
    const { languages, error } = await CompanyService.updateCompanyLanguages(languageNames);
    if (!languages) {
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update info, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));

    dispatch(CoreSliceActions.loadLanguagesSuccess(languages));
    return languages;
  }
);

export const createCompanySpecialtySpecialisations = createAsyncThunk(
  ECompanyProfileThunks.CreateCompanySpecialtySpecialisations,
  async (data: ICreateSpecialtySpecialisations, { dispatch }) => {
    const { company_specializations, error } = await CompanyService.createCompanySpecialtySpecialisations(data);
    if (!company_specializations) {
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update info, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(fetchSpecialities());
    dispatch(CompanySliceActions.addCompanySpecialtySpecialisation(company_specializations));
    return company_specializations;
  }
);

export const updateCompanySpecialtySpecialisations = createAsyncThunk(
  ECompanyProfileThunks.UpdateCompanySpecialtySpecialisations,
  async (data: IUpdateSpecialtySpecialisations, { dispatch }) => {
    const { company_specializations, error } = await CompanyService.updateCompanySpecialtySpecialisations(data);
    if (!company_specializations) {
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update info, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(fetchSpecialities());
    dispatch(CompanySliceActions.updateCompanySpecialtySpecialisation(company_specializations));
    return company_specializations;
  }
);

export const deleteCompanySpecialtySpecialisations = createAsyncThunk(
  ECompanyProfileThunks.DeleteCompanySpecialtySpecialisations,
  async (speciality_id: number, { dispatch }) => {
    const {
      company_specializations,
      error
    } = await CompanyService.deleteCompanySpecialtySpecialisations(speciality_id);
    if (!company_specializations) {
      dispatch(openNotification({
        description: error ?? 'Couldn\'t delete info, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(fetchSpecialities());
    dispatch(CompanySliceActions.deleteCompanySpecialtySpecialisation(speciality_id));
    return company_specializations;
  }
);

export const updateCompanyBilling = createAsyncThunk(
  ECompanyProfileThunks.UpdateCompanyBilling,
  async ({ is_not_enough_minutes, is_not_enough_request }:
    { is_not_enough_minutes: boolean, is_not_enough_request: boolean }, { dispatch }) => {
    dispatch(CompanySliceActions.updateCompanyBilling({ is_not_enough_minutes, is_not_enough_request }));
  }
);

export const updateCompanyCounsellorManagement = createAsyncThunk(
  ECompanyProfileThunks.UpdateCompanyCounsellorManagement,
  async (state: boolean, { dispatch }) => {
    dispatch(CompanySliceActions.updateCompanyCounsellorManagement(state));
  }
);

export const updateCompanyCounsellorAttributes = createAsyncThunk(
  ECompanyProfileThunks.UpdateCompanyCounsellorAttributes,
  async (state: boolean, { dispatch }) => {
    dispatch(CompanySliceActions.updateCompanyCounsellorAttributes(state));
  }
);
